import React, { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { LeftOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';

const isDark = (theme) => theme?.colors?.background?.default === '#141414';

const CHIP_SCROLL_STEP = 220;
/** Pixels from strip right edge; crossing in triggers one older GET /chat page (when provided). */
const NEAR_END_SCROLL_PX = 64;

/**
 * Horizontal chip row: scrollbar hidden; scroll with trackpad, chevrons, or arrow keys.
 */
const ChipStrip = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 2px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const ScrollChevron = styled.button`
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)')};
    background: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')};
    color: ${({ $dark }) => ($dark ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.72)')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: 2px solid ${({ $dark }) => ($dark ? 'rgba(139,92,246,0.85)' : 'rgba(124,58,237,0.65)')};
        outline-offset: 2px;
    }
`;

export function ThreadTopBar({
    threads = [],
    selectedThreadId = null,
    onThreadTap,
    /** When true, older chat message pages exist (GET chat pagination). */
    hasMoreChatPages = false,
    /** Fetch next page of chat messages (older); new parents may add thread chips. */
    onLoadMoreChatPages,
    isLoadingMoreChatPages = false,
}) {
    const theme = useTheme();
    const dark = isDark(theme);
    const stripRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    /** Re-arm scroll-triggered load after user scrolls away from the right edge. */
    const scrollEndLoadArmedRef = useRef(true);

    const items = useMemo(() => (Array.isArray(threads) ? threads : []).filter(Boolean), [threads]);

    const updateScrollHints = useCallback(() => {
        const strip = stripRef.current;
        if (!strip) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        const { scrollLeft, scrollWidth, clientWidth } = strip;
        const max = Math.max(0, scrollWidth - clientWidth);
        setCanScrollLeft(scrollLeft > 2);
        setCanScrollRight(scrollLeft < max - 2);

        const canLoadMore = Boolean(hasMoreChatPages && onLoadMoreChatPages && !isLoadingMoreChatPages);
        const nearRightEnd = max > NEAR_END_SCROLL_PX && scrollLeft >= max - NEAR_END_SCROLL_PX;
        if (!nearRightEnd) {
            scrollEndLoadArmedRef.current = true;
        } else if (canLoadMore && scrollEndLoadArmedRef.current) {
            scrollEndLoadArmedRef.current = false;
            onLoadMoreChatPages();
        }
    }, [hasMoreChatPages, isLoadingMoreChatPages, onLoadMoreChatPages]);

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return;
        updateScrollHints();
        strip.addEventListener('scroll', updateScrollHints, { passive: true });
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => updateScrollHints());
            ro.observe(strip);
        }
        return () => {
            strip.removeEventListener('scroll', updateScrollHints);
            ro?.disconnect();
        };
    }, [items.length, updateScrollHints]);

    const scrollStripBy = useCallback((delta) => {
        const strip = stripRef.current;
        if (!strip) return;
        strip.scrollBy({ left: delta, behavior: 'smooth' });
    }, []);

    const handleRightChevronClick = useCallback(() => {
        if (canScrollRight) {
            scrollStripBy(CHIP_SCROLL_STEP);
            return;
        }
        if (hasMoreChatPages && !isLoadingMoreChatPages && onLoadMoreChatPages) {
            onLoadMoreChatPages();
        }
    }, [
        canScrollRight,
        hasMoreChatPages,
        isLoadingMoreChatPages,
        onLoadMoreChatPages,
        scrollStripBy,
    ]);

    const onBarKeyDownCapture = useCallback(
        (e) => {
            const strip = stripRef.current;
            if (!strip) return;
            const el = e.target;
            if (el && typeof el.closest === 'function' && el.closest('input, textarea, [contenteditable="true"]')) return;
            if (!strip.contains(el)) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                scrollStripBy(-CHIP_SCROLL_STEP);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const max = Math.max(0, strip.scrollWidth - strip.clientWidth);
                const atStripEnd = max <= 8 || strip.scrollLeft >= max - 2;
                if (!atStripEnd) scrollStripBy(CHIP_SCROLL_STEP);
                else if (hasMoreChatPages && !isLoadingMoreChatPages && onLoadMoreChatPages) onLoadMoreChatPages();
            } else if (e.key === 'Home') {
                e.preventDefault();
                strip.scrollTo({ left: 0, behavior: 'smooth' });
            } else if (e.key === 'End') {
                e.preventDefault();
                strip.scrollTo({ left: strip.scrollWidth, behavior: 'smooth' });
            }
        },
        [scrollStripBy, hasMoreChatPages, isLoadingMoreChatPages, onLoadMoreChatPages],
    );

    if (items.length === 0) return null;

    return (
        <div
            style={{
                flex: '0 0 auto',
                borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                padding: '10px 12px',
                background: dark ? '#1f1f1f' : '#fff',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                }}
                onKeyDownCapture={onBarKeyDownCapture}
            >
                <ScrollChevron
                    type="button"
                    $dark={dark}
                    aria-label="Scroll thread tabs left"
                    disabled={!canScrollLeft}
                    onClick={() => scrollStripBy(-CHIP_SCROLL_STEP)}
                >
                    <LeftOutlined style={{ fontSize: 12 }} />
                </ScrollChevron>
                <ChipStrip
                    ref={stripRef}
                    role="list"
                    aria-label="Threads. Scroll with trackpad, the side buttons, or arrow keys while a thread tab is focused."
                >
                    {items.map((t) => {
                        const id = t.id ?? t.threadId ?? t.parentId;
                        const active = selectedThreadId != null && String(selectedThreadId) === String(id);
                        const title = String(t.title || '').trim() || 'Untitled Thread';
                        const count = typeof t.count === 'number' ? t.count : 0;
                        return (
                            <button
                                key={String(id)}
                                type="button"
                                role="listitem"
                                onClick={() => onThreadTap?.(t)}
                                style={{
                                    border: `1px solid ${
                                        active
                                            ? dark
                                                ? 'rgba(139,92,246,0.75)'
                                                : 'rgba(139,92,246,0.55)'
                                            : dark
                                              ? 'rgba(255,255,255,0.14)'
                                              : 'rgba(0,0,0,0.10)'
                                    }`,
                                    background: active
                                        ? dark
                                            ? 'rgba(139,92,246,0.22)'
                                            : 'rgba(139,92,246,0.14)'
                                        : dark
                                          ? 'rgba(255,255,255,0.06)'
                                          : 'rgba(0,0,0,0.03)',
                                    color: dark ? 'rgba(255,255,255,0.92)' : '#111',
                                    borderRadius: 999,
                                    padding: '6px 12px',
                                    fontSize: 12,
                                    fontWeight: 900,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    maxWidth: 260,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flexShrink: 0,
                                }}
                                title={title}
                            >
                                <span
                                    style={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {title}
                                </span>
                                {count > 0 ? (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: 18,
                                            height: 18,
                                            padding: '0 6px',
                                            borderRadius: 999,
                                            background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                                            color: dark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.7)',
                                            fontSize: 11,
                                            fontWeight: 900,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {count}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                </ChipStrip>
                <ScrollChevron
                    type="button"
                    $dark={dark}
                    aria-label={
                        canScrollRight
                            ? 'Scroll thread tabs right'
                            : hasMoreChatPages
                              ? 'Load older threads'
                              : 'Scroll thread tabs right'
                    }
                    disabled={!canScrollRight && !(hasMoreChatPages && !isLoadingMoreChatPages)}
                    onClick={handleRightChevronClick}
                >
                    {isLoadingMoreChatPages ? (
                        <LoadingOutlined spin style={{ fontSize: 12 }} />
                    ) : (
                        <RightOutlined style={{ fontSize: 12 }} />
                    )}
                </ScrollChevron>
            </div>
        </div>
    );
}
